import { create } from 'zustand'
import {
    changeEmail,
    changePassword,
    deleteUser,
    emailVerifyResendCode,
    getUserData,
    login,
    passwordReset,
    passwordResetComplete,
    passwordResetGetToken,
    patchUserData,
    registration,
    verifyEmail,
} from '../api/user'

import {
    BodyVerifyUserEmailCompleteApiV1AuthUserEmailVerifyCompletePost,
    EmailVerifyResendModel,
    PasswordChangeRequest,
    UserDeleteModel,
    UserInfoModel,
    UserPasswordResetCompleteModel,
    UserPasswordResetCompleteServiceOutputDTO,
    UserPasswordResetGetTokenModel,
    UserPasswordResetGetTokenServiceOutputDTO,
    UserPasswordResetModel,
    UserPasswordResetStartServiceOutputDTO,
    UserRegisterModel,
} from '../swagger/dto'

interface Actions {
    login: (params: { email: string; password: string }) => string
    registration: (data: UserRegisterModel) => void
    verifyEmail: (data: BodyVerifyUserEmailCompleteApiV1AuthUserEmailVerifyCompletePost) => void
    passwordReset: (data: UserPasswordResetModel) => Promise<UserPasswordResetStartServiceOutputDTO>
    passwordResetGetToken: (
        data: UserPasswordResetGetTokenModel,
    ) => Promise<UserPasswordResetGetTokenServiceOutputDTO>
    passwordResetComplete: (
        data: UserPasswordResetCompleteModel,
    ) => Promise<UserPasswordResetCompleteServiceOutputDTO>
    emailVerifyResendCode: (data: EmailVerifyResendModel) => void
    setUser: (user: string) => void
    updateUserPersonalData: (user: Partial<UserInfoModel>) => Promise<void>
    getUserPersonalData: () => Promise<void>
    clearUserPersonalData: () => void
    changeEmail: (email: string) => Promise<void>
    setUserPersonalData: (user: Partial<UserInfoModel>) => void
    changePassword: (params: PasswordChangeRequest) => Promise<void>
    deleteUser: (data: UserDeleteModel) => Promise<void>
    setErrorDeleteUser: (error?: string) => void
    setFirstStepRegistration: () => void
    reset: () => void
}

interface State {
    error?: string
    isUserPersonalDataFetched: boolean
    userPersonalData?: Partial<UserInfoModel>
    user?: string
    email_verify_id?: string
    token?: string
    isLoading: boolean
    firstStepRegistration: boolean
    secondStepRegistration: boolean
    firstStepPasswordReset: boolean
    secondStepPasswordReset: boolean
    isResendCode: boolean
    isDonePasswordReset: boolean
    isDoneDeleteUser: boolean
    isLoadingDeleteUser: boolean
    errorDeleteUser?: string
    isLoadingChangeEmail: boolean
    errorChangeEmail?: string
}

const initialState: State = {
    user: undefined,
    token: undefined,
    isUserPersonalDataFetched: false,
    userPersonalData: {
        profile_pic: '',
        firstname: '',
        lastname: '',
    },
    email_verify_id: undefined,
    error: undefined,
    isLoading: false,
    firstStepRegistration: false,
    secondStepRegistration: false,
    firstStepPasswordReset: false,
    secondStepPasswordReset: false,
    isDonePasswordReset: false,
    isResendCode: false,
    isDoneDeleteUser: false,
    isLoadingDeleteUser: false,
    errorDeleteUser: undefined,
    isLoadingChangeEmail: false,
    errorChangeEmail: undefined,
}

export const useUserStore = create<State & Actions>()((set, get) => ({
    ...initialState,
    login: async (params) => {
        try {
            set({
                isLoading: true,
                error: undefined,
            })
            const data = await login(params)
            set({
                user: data?.token,
                error: undefined,
            })
        } catch (error) {
            set({
                error: 'Неверный логин или пароль',
            })
        } finally {
            set({
                isLoading: false,
            })
        }
    },

    registration: async (data) => {
        try {
            set({
                isLoading: true,
                error: undefined,
                firstStepRegistration: false,
            })
            const response = await registration(data)
            set({
                email_verify_id: response.email_verify_id,
                firstStepRegistration: true,
                isLoading: false,
            })
        } catch (error) {
            set({
                isLoading: false,
                error: error,
            })
        }
    },

    verifyEmail: async (data) => {
        try {
            set({
                isLoading: true,
                error: undefined,
                secondStepRegistration: false,
            })
            const response = await verifyEmail(data)
            set({
                token: response.token,
                secondStepRegistration: true,
                isLoading: false,
                email_verify_id: undefined,
                error: undefined,
            })
        } catch (error) {
            set({
                isLoading: false,
                error: error?.response?.data?.detail?.error_message,
            })
        }
    },

    passwordReset: async (data) => {
        try {
            set({
                isLoading: true,
                error: undefined,
                firstStepPasswordReset: false,
            })
            const response = await passwordReset(data)
            set({
                email_verify_id: response.email_verify_id,
                firstStepPasswordReset: true,
                isLoading: false,
            })
        } catch (error) {
            set({
                isLoading: false,
                error: error,
            })
        }
    },

    passwordResetGetToken: async (data) => {
        try {
            set({
                isLoading: true,
                error: undefined,
            })
            const response = await passwordResetGetToken(data)
            set({
                token: response.reset_token,
                isLoading: false,
            })
        } catch (error) {
            set({
                isLoading: false,
                error: error,
            })
        }
    },
    passwordResetComplete: async (data) => {
        try {
            set({
                isLoading: true,
                error: undefined,
                isDonePasswordReset: false,
            })
            await passwordResetComplete(data)
            set({
                isDonePasswordReset: true,
                isLoading: false,
            })
        } catch (error) {
            set({
                isLoading: false,
                error: error,
            })
        }
    },

    emailVerifyResendCode: async (data) => {
        try {
            set({
                isLoading: true,
                error: undefined,
                isResendCode: false,
            })
            await emailVerifyResendCode(data)
            set({
                isResendCode: true,
                isLoading: false,
            })
        } catch (error) {
            set({
                isLoading: false,
                error: error?.response?.data?.detail?.error_message,
            })
        }
    },
    setUser: (user) => {
        set({
            user,
        })
    },
    setUserPersonalData: (userData: Partial<UserInfoModel>) =>
        set({ userPersonalData: { ...get().userPersonalData, ...userData } }),
    clearUserPersonalData: () => set({ userPersonalData: undefined }),
    setFirstStepRegistration: () =>
        set({
            firstStepRegistration: true,
            secondStepRegistration: false,
            email_verify_id: undefined,
        }),
    updateUserPersonalData: async (user: Partial<UserInfoModel>, isUpdate = true) => {
        try {
            set({
                error: undefined,
            })

            const updatedUserData = await patchUserData(user)
            if (isUpdate) {
                const userPersonalData = { ...get().userPersonalData, ...updatedUserData }
                set({ userPersonalData })
            }
        } catch (error) {
            set({
                error: error?.response?.data?.detail?.error_message,
            })
        }
    },
    getUserPersonalData: async () => {
        try {
            set({
                error: undefined,
                isUserPersonalDataFetched: false,
            })

            const userData = await getUserData()
            const oldUserData = get().userPersonalData

            set({
                userPersonalData: {
                    ...oldUserData,
                    ...userData,
                    is_new: oldUserData?.is_new || userData?.is_new, // save state between requests for the current session
                },
                isUserPersonalDataFetched: true,
            })
        } catch (error) {
            set({
                error: error?.response?.data?.detail?.error_message,
            })
        }
    },
    changeEmail: async (email: string) => {
        try {
            set({
                errorChangeEmail: undefined,
                isLoadingChangeEmail: true,
                firstStepRegistration: false,
            })

            const { email_verify_id } = await changeEmail(email)

            set({
                isLoadingChangeEmail: false,
                email_verify_id,
            })
        } catch (error) {
            set({
                isLoadingChangeEmail: false,
                errorChangeEmail:
                    error?.response?.data?.detail?.error_code === 'IAMDomainError'
                        ? 'Пользователь с таким email уже существует'
                        : 'Возникла ошибка!',
            })
        }
    },
    changePassword: async (params: PasswordChangeRequest) => {
        try {
            set({
                error: undefined,
            })

            await changePassword(params)
        } catch (error) {
            set({
                error: error?.response?.data?.detail?.error_message,
            })
        }
    },
    deleteUser: async (data: UserDeleteModel) => {
        try {
            set({
                isDoneDeleteUser: false,
                isLoadingDeleteUser: true,
                errorDeleteUser: undefined,
            })

            await deleteUser(data)
            set({
                isDoneDeleteUser: true,
                isLoadingDeleteUser: false,
            })
        } catch (error) {
            if (error?.response?.data?.detail?.error_message === 'Invalid password') {
                set({
                    isLoadingDeleteUser: false,
                    errorDeleteUser: 'Неверный пароль',
                })
            } else {
                set({
                    isLoadingDeleteUser: false,
                    errorDeleteUser: 'Возникла ошибка',
                })
            }
        }
    },
    setErrorDeleteUser: (error) => {
        set({
            errorDeleteUser: error,
        })
    },
    reset: () => {
        set(initialState)
    },
}))
